import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BRAND } from "../lib/assets";
import SectionLabel from "../components/SectionLabel";

const TIMELINE = [
  {
    period: "Education",
    title: "Government College of Art & Craft, Kolkata",
    body: "Foundational training in fine art, craft, and visual systems.",
  },
  {
    period: "Business",
    title: "XIM Bhubaneswar",
    body: "Business and management training that pairs creative depth with commercial intent.",
  },
  {
    period: "Practice / 35+ years",
    title: "Brand identity, retail, exhibitions, integrated systems",
    body: "Over three decades of work across complex, real-world business challenges.",
  },
];

const PHILOSOPHY = [
  { n: "01", line: "Clarity over decoration." },
  { n: "02", line: "Structure over randomness." },
  { n: "03", line: "Execution over intent alone." },
];

const Founder = () => {
  return (
    <div data-testid="page-founder" className="pt-32 md:pt-40">
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <SectionLabel className="mb-8" testid="label-founder">
          [ FOUNDER ] - UTPAL BISWAS
        </SectionLabel>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: [0.22, 0.61, 0.36, 1] }}
          className="font-archivo font-black text-black h-tighter text-5xl md:text-6xl lg:text-7xl max-w-5xl"
          style={{ fontFamily: "Archivo, sans-serif" }}
        >
          A brand is not defined by isolated visuals - but by how
          <span className="text-[#AE519F]"> consistently it is built and executed </span>
          across every touchpoint.
        </motion.h1>
      </section>

      {/* Portrait + Bio */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="lg:col-span-5 relative"
          >
            <div className="relative border border-black/40 bg-[#CCD6DC] overflow-hidden">
              {/* corner ticks */}
              <div className="absolute top-0 left-0 w-3 h-3 border-l border-t border-black" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r border-t border-black" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-black" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-black" />

              <div className="relative w-full aspect-[4/5] overflow-hidden">
                <img
                  src={BRAND.founder}
                  alt="Utpal Biswas - Founder, UPRCSE"
                  className="absolute inset-0 w-full h-full object-cover object-center proj-img"
                  data-testid="founder-portrait"
                />
              </div>
              <div className="px-5 py-4 border-t border-black/30 flex items-center justify-between">
                <div>
                  <div className="mono-label">FOUNDER</div>
                  <div
                    className="text-black text-lg mt-1"
                    style={{ fontFamily: "Archivo, sans-serif", fontWeight: 700 }}
                  >
                    Utpal Biswas
                  </div>
                </div>
                <div className="mono-label text-[#AE519F]">SINCE 2000+</div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="mono-label mb-4">PROFILE</div>
              <p
                className="text-black text-lg md:text-xl leading-relaxed"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Utpal Biswas brings over three decades of experience across
                branding, communication, and large-scale execution. A graduate
                of <span className="text-[#AE519F]">Government College of Art & Craft, Kolkata</span>,
                with further business training from <span className="text-[#AE519F]">XIM Bhubaneswar</span>,
                his journey combines creative depth with business understanding.
              </p>
            </div>

            <div>
              <div className="mono-label mb-4">DOMAINS</div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/30">
                {[
                  "Brand identity development",
                  "Retail and exhibition environments",
                  "Integrated communication systems",
                  "Large-scale execution projects",
                ].map((d, i) => (
                  <li
                    key={d}
                    className="bg-[#CCD6DC] p-5 flex items-center gap-4 group hover:bg-[#FFBBFF]/40 transition-colors"
                  >
                    <span className="mono-label group-hover:text-[#AE519F] transition-colors">
                      {`0${i + 1}`}
                    </span>
                    <span
                      className="text-black text-base"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {d}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Signature line */}
            <div className="pt-8 border-t border-black/30">
              <div className="mono-label mb-3">SIGNATURE</div>
              <p
                className="text-[#AE519F] text-2xl md:text-3xl italic"
                style={{
                  fontFamily: "Archivo, sans-serif",
                  fontWeight: 500,
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                }}
              >
                "Shaping how businesses are understood,
                <br />
                not just how they appear."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline - Utpal Biswas journey */}
      <section className="border-t-2 border-black py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionLabel className="mb-6" testid="label-timeline">
            [ TIMELINE ] - JOURNEY
          </SectionLabel>
          <h2
            className="font-archivo font-black text-black h-tighter text-4xl md:text-5xl lg:text-6xl mb-14"
            style={{ fontFamily: "Archivo, sans-serif" }}
          >
            Three decades, mapped
            <br />
            <span className="text-[#AE519F]">on a structural grid.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/30">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="bg-[#CCD6DC] p-7 md:p-8"
              >
                <div className="mono-label mb-5 text-[#AE519F]">{t.period}</div>
                <h3
                  className="text-black text-xl md:text-2xl mb-4"
                  style={{
                    fontFamily: "Archivo, sans-serif",
                    fontWeight: 800,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.1,
                  }}
                >
                  {t.title}
                </h3>
                <p
                  className="text-black/70 text-base"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {t.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Senior Advisor - Kuntal Biswas */}
      <section
        data-testid="section-advisor"
        className="border-t-2 border-black py-20 md:py-28"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionLabel className="mb-8" testid="label-advisor">
            [ ADVISORY ] - SENIOR ADVISOR
          </SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="relative border border-black/40 bg-[#CCD6DC] overflow-hidden">
                {/* corner ticks */}
                <div className="absolute top-0 left-0 w-3 h-3 border-l border-t border-black" />
                <div className="absolute top-0 right-0 w-3 h-3 border-r border-t border-black" />
                <div className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-black" />
                <div className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-black" />

                <div className="relative w-full aspect-[4/5] overflow-hidden">
                  <img
                    src={BRAND.advisor}
                    alt="Kuntal Biswas - Senior Advisor, Brand & Growth, UPRCSE"
                    className="absolute inset-0 w-full h-full object-cover object-center proj-img"
                    data-testid="advisor-portrait"
                  />
                </div>
                <div className="px-5 py-4 border-t border-black/30 flex items-center justify-between">
                  <div>
                    <div className="mono-label">SENIOR ADVISOR</div>
                    <div
                      className="text-black text-lg mt-1"
                      style={{ fontFamily: "Archivo, sans-serif", fontWeight: 700 }}
                    >
                      Kuntal Biswas
                    </div>
                  </div>
                  <div className="mono-label text-[#AE519F]">BRAND · GROWTH</div>
                </div>
              </div>
            </motion.div>

            {/* Body */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-8"
            >
              <div>
                <div className="mono-label mb-3">ROLE</div>
                <h2
                  className="font-archivo font-black text-black h-tighter text-3xl md:text-4xl lg:text-5xl"
                  style={{ fontFamily: "Archivo, sans-serif" }}
                >
                  Senior Advisor -
                  <br />
                  <span className="text-[#AE519F]">Brand & Growth.</span>
                </h2>
              </div>

              <div className="space-y-5">
                <p
                  className="text-black text-lg md:text-xl leading-relaxed"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  Kuntal Biswas is a seasoned brand and business-growth
                  professional with extensive experience across media,
                  advertising, and brand solutions.
                </p>
                <p
                  className="text-black/80 text-base md:text-lg leading-relaxed"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  From 2012 to 2026, he served as <span className="text-[#AE519F]">Head of Brand for Ei Samay and Navbharat Times</span>,
                  the language publications of <span className="text-[#AE519F]">The Times Group</span>,
                  where he spearheaded initiatives focused on business growth,
                  brand development, and brand equity.
                </p>
                <p
                  className="text-black/80 text-base md:text-lg leading-relaxed"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  He has also worked with <span className="text-[#AE519F]">Ogilvy</span> and
                  other leading agency networks, contributing to campaigns and
                  brand solutions for prominent brands including <span className="text-[#AE519F]">Vodafone</span> and
                  <span className="text-[#AE519F]"> Tata Steel</span>, as well as clients across the retail sector.
                </p>
                <p
                  className="text-black/80 text-base md:text-lg leading-relaxed"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  At UPRCSE, Kuntal serves as Senior Advisor - Brand & Growth,
                  bringing his expertise in brand strategy, business growth,
                  communications, and market development to support the
                  company's growth and evolving business vision.
                </p>
              </div>

              <div>
                <div className="mono-label mb-4">FOCUS AREAS</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/30">
                  {[
                    "Brand strategy",
                    "Business growth",
                    "Communications",
                    "Market development",
                  ].map((d, i) => (
                    <li
                      key={d}
                      className="bg-[#CCD6DC] p-5 flex items-center gap-4 group hover:bg-[#FFBBFF]/40 transition-colors"
                      data-testid={`advisor-focus-${i}`}
                    >
                      <span className="mono-label group-hover:text-[#AE519F] transition-colors">
                        {`0${i + 1}`}
                      </span>
                      <span
                        className="text-black text-base"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {d}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="border-t-2 border-black py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionLabel className="mb-6" testid="label-philosophy">
                [ PHILOSOPHY ]
              </SectionLabel>
              <h2
                className="font-archivo font-black text-black h-tighter text-4xl md:text-5xl"
                style={{ fontFamily: "Archivo, sans-serif" }}
              >
                Three rules, in order.
              </h2>
              <p
                className="mt-8 text-black/70 max-w-md"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                A brand is built through consistency, discipline, and alignment -
                not through disconnected efforts.
              </p>
            </div>
            <div className="lg:col-span-7 space-y-px bg-black/30">
              {PHILOSOPHY.map((p, i) => (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.55, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-[#CCD6DC] p-7 md:p-8 grid grid-cols-12 items-center gap-5 group hover:bg-[#FFBBFF]/40"
                >
                  <div className="col-span-2 md:col-span-1 mono-label">{p.n}</div>
                  <div className="col-span-10 md:col-span-11">
                    <div
                      className="text-black text-2xl md:text-3xl group-hover:text-[#AE519F] transition-colors"
                      style={{
                        fontFamily: "Archivo, sans-serif",
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {p.line}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p
                className="text-black text-xl md:text-2xl"
                style={{ fontFamily: "Outfit, sans-serif", lineHeight: 1.4 }}
              >
                UPRCSE is not a volume-driven practice.
                <br />
                <span className="text-[#AE519F]">
                  Every engagement is built with involvement, intent, and long-term value.
                </span>
              </p>
            </div>
            <div className="lg:col-span-5 lg:flex lg:justify-end">
              <Link
                to="/connect"
                data-testid="founder-cta"
                className="liquid-btn is-primary inline-flex items-center gap-3"
              >
                Begin a Conversation
                <ArrowUpRight size={16} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Founder;
